import moment from 'moment';
import OperatingHoursMapper from '@/classes/OperatingHoursMapper';
import { InstructorTableRowType } from '@/enums/InstructorTableRowType';
import { InstructorTableCellType } from '@/enums/InstructorTableCellType';
import InstructorTableInstructorRow from '@/classes/InstructorTableInstructorRow';
import InstructorTableBlockRow from '@/classes/InstructorTableBlockRow';
import InstructorTableAvailableCell from '@/classes/InstructorTableAvailableCell';
import InstructorTableReservedCell from '@/classes/InstructorTableReservedCell';
import InstructorTablePauseCell from '@/classes/InstructorTablePauseCell';
import store from '@/store/index';
import { TRANSACTION_CATEGORY } from "@/constants/TransactionCategories";
import { BALANCES } from "@/constants/Balances";
import InstructorTableKidSchool from '@/classes/InstructorTableKidSchool';
import InstructorTableKidSchoolCell from './InstructorTableKidSchoolCell';
import { SERVICE_TYPE } from "@/constants/ServiceTypes";
export default class InstructorTable {
    id;
    date;
    changedAt;
    fromHour;
    toHour;
    concluded;
    pauseHours = [];
    rows = [];
    kidSchools = [];
    toSaveObject() {
        return {
            date: this.date.format('YYYY-MM-DD'),
            concluded: this.concluded,
            changedAt: moment().toISOString(),
            rows: this.rows.map(x => x.toSaveObject())
        };
    }
    getOrderedRows(showOtherInstructors) {
        let result = this.rows;
        result = result.filter(x => x.rowType === InstructorTableRowType.BLOCK || x.cells.some(y => y.cellType !== InstructorTableCellType.PAUSE)
            || (x.rowType === InstructorTableRowType.INSTRUCTOR && x.isCurrentUserRow()));
        if (!showOtherInstructors) {
            result = result.filter((x) => x.rowType !== InstructorTableRowType.INSTRUCTOR ||
                x.isCurrentUserRow());
        }
        return result.sort((a, b) => {
            const showCurrentUserOnFirstPlace = false;
            let aType;
            let bType;
            aType = a.rowType;
            bType = b.rowType;
            const currentUser = 'currentUser';
            if (showCurrentUserOnFirstPlace && a.rowType === InstructorTableRowType.INSTRUCTOR &&
                a.isCurrentUserRow()) {
                aType = currentUser;
            }
            if (showCurrentUserOnFirstPlace && b.rowType === InstructorTableRowType.INSTRUCTOR &&
                b.isCurrentUserRow()) {
                bType = currentUser;
            }
            switch (aType + bType) {
                case `${InstructorTableRowType.BLOCK}${InstructorTableRowType.BLOCK}`:
                    return (a.blockId < b.blockId) ? -1 : 1;
                case `${InstructorTableRowType.INSTRUCTOR}${InstructorTableRowType.INSTRUCTOR}`:
                    return (a.addedDate < b.addedDate) ? -1 : 1;
                case `${currentUser}${InstructorTableRowType.BLOCK}`:
                case `${currentUser}${InstructorTableRowType.INSTRUCTOR}`:
                case `${InstructorTableRowType.INSTRUCTOR}${InstructorTableRowType.BLOCK}`: {
                    return -1;
                }
                case `${InstructorTableRowType.BLOCK}${InstructorTableRowType.INSTRUCTOR}`:
                case `${InstructorTableRowType.BLOCK}${currentUser}`:
                case `${InstructorTableRowType.INSTRUCTOR}${currentUser}`:
                    return 1;
            }
            return 0;
        });
    }
    async conclude() {
        const transactionBatch = [];
        const tableDateTime = this.date.set({ hour: this.fromHour, minute: 0, second: 0 });
        const currentDateTime = moment().format('YYYY-MM-DD HH:mm:ss');
        const payouts = {
            'individual': TRANSACTION_CATEGORY.PAYOUT_INDIVIDUAL,
            'wex': TRANSACTION_CATEGORY.PAYOUT_INDIVIDUAL,
            'staff': TRANSACTION_CATEGORY.PAYOUT_STAFF,
            'kid_school': TRANSACTION_CATEGORY.PAYOUT_KID_SCHOOL,
            'storno': TRANSACTION_CATEGORY.STORNO,
        };
        for (let kidSchool of this.kidSchools) {
            const school = await store.dispatch('kidSchool/getById', kidSchool.id);
            if (!school) {
                console.error('Dětská školka nenalezena');
                continue;
            }
            let income;
            do {
                income = Number(prompt(`Kolik peněz bylo utrženo za školku od ${moment(school.dateTime).format('HH:mm')}`, '0'));
            } while (income === 0);
            store.dispatch('kidSchool/update', {
                id: school.id,
                capacity: school.capacity,
                datetime: school.dateTime,
                center: process.env.VUE_APP_AREAL_ID,
                age: school.age,
                price: school.price,
                income: income,
                isPremium: school.isPremium,
                leaderId: school.leaderId,
            });
            transactionBatch.push({
                id: 'new-transaction',
                data: {
                    from: 'cash',
                    to: BALANCES.MAIN_BALANCE,
                    value: income,
                    dateTime: tableDateTime.set({ hour: kidSchool.hourFrom }).format('YYYY-MM-DD HH:mm:ss'),
                    createdAt: currentDateTime,
                    category: SERVICE_TYPE.KID_SCHOOL,
                    description: 'Příjem z dětské školky',
                    deletedAt: null,
                },
            });
            const lessonLengthInHours = Math.ceil(kidSchool.duration / 60) + (kidSchool.isPremium ? 1 : 0);
            for (const instructorId of kidSchool.instructorIds) {
                const instructor = store.getters['instructor/get'](instructorId);
                if (!instructor) {
                    throw new Error(`Instruktor nenalezen`);
                }
                let value = instructor.calculatePayout(SERVICE_TYPE.KID_SCHOOL, lessonLengthInHours);
                if (instructorId === kidSchool.leaderId) {
                    value += 50;
                }
                transactionBatch.push({
                    id: 'new-transaction',
                    data: {
                        from: BALANCES.MAIN_BALANCE,
                        to: instructorId,
                        value,
                        dateTime: tableDateTime.set({ hour: kidSchool.hourFrom }).format('YYYY-MM-DD HH:mm:ss'),
                        createdAt: currentDateTime,
                        category: SERVICE_TYPE.KID_SCHOOL,
                        description: 'Mzda - Dětská školka',
                        deletedAt: null,
                    },
                });
            }
        }
        for (const row of [...this.rows]) {
            if (row.rowType === InstructorTableRowType.BLOCK) {
                this.rows.splice(this.rows.indexOf(row), 1);
                continue;
            }
            const instructor = store.getters['instructor/get'](row.instructorId);
            if (!instructor) {
                throw new Error(`Instruktor nenalezen`);
            }
            for (const cell of [...row.cells]) {
                if (cell.cellType === InstructorTableCellType.AVAILABLE) {
                    row.cells.splice(row.cells.indexOf(cell), 1);
                }
                else if (cell.cellType === InstructorTableCellType.RESERVED) {
                    if (cell.isPrepaid) {
                        transactionBatch.push({
                            id: 'new-transaction',
                            data: {
                                from: 'cash',
                                to: BALANCES.MAIN_BALANCE,
                                value: cell.priceIncludingDiscount,
                                dateTime: tableDateTime.set({ hour: cell.hour }).format('YYYY-MM-DD HH:mm:ss'),
                                createdAt: currentDateTime,
                                category: cell.service,
                                description: 'Příjem z výuky - platba předem',
                                deletedAt: null,
                            },
                        });
                        transactionBatch.push({
                            id: 'new-transaction',
                            data: {
                                from: BALANCES.MAIN_BALANCE,
                                to: row.instructorId,
                                value: instructor.calculatePayout(cell.lessonType, cell.length),
                                dateTime: tableDateTime.set({ hour: cell.hour }).format('YYYY-MM-DD HH:mm:ss'),
                                createdAt: currentDateTime,
                                category: payouts[cell.lessonType],
                                description: 'Mzda',
                                deletedAt: null,
                            },
                        });
                    }
                    else {
                        transactionBatch.push({
                            id: 'new-transaction',
                            data: {
                                from: 'cash',
                                to: BALANCES.MAIN_BALANCE,
                                value: cell.price,
                                dateTime: tableDateTime.set({ hour: cell.hour }).format('YYYY-MM-DD HH:mm:ss'),
                                createdAt: currentDateTime,
                                category: cell.service,
                                description: 'Příjem z výuky - platba v hotovosti',
                                deletedAt: null,
                            },
                        });
                        transactionBatch.push({
                            id: 'new-transaction',
                            data: {
                                from: 'cash',
                                to: row.instructorId,
                                value: -cell.priceIncludingDiscount,
                                dateTime: tableDateTime.set({ hour: cell.hour }).format('YYYY-MM-DD HH:mm:ss'),
                                createdAt: currentDateTime,
                                category: cell.service,
                                description: 'Příjem z výuky - platba v hotovosti',
                                deletedAt: null,
                            },
                        });
                        transactionBatch.push({
                            id: 'new-transaction',
                            data: {
                                from: BALANCES.MAIN_BALANCE,
                                to: row.instructorId,
                                value: instructor.calculatePayout(cell.lessonType, cell.length),
                                dateTime: tableDateTime.set({ hour: cell.hour }).format('YYYY-MM-DD HH:mm:ss'),
                                createdAt: currentDateTime,
                                category: payouts[cell.lessonType],
                                description: 'Mzda',
                                deletedAt: null,
                            },
                        });
                    }
                }
            }
        }
        this.concluded = true;
        await store.dispatch('instructorTable/update', this);
        await store.dispatch('transaction/store', transactionBatch);
    }
    static loadFromObject(object, id) {
        const result = new InstructorTable();
        result.id = id;
        result.date = moment(object.date, 'YYYY-MM-DD');
        result.changedAt = object.changedAt ?? '';
        result.concluded = object.concluded;
        const hours = OperatingHoursMapper.getByDay(result.date);
        result.fromHour = hours.openFrom;
        result.toHour = hours.openTo;
        result.pauseHours = hours.pauses;
        result.rows = object.rows?.map((x) => this.loadRowFromObject(x, result, result.pauseHours ?? [])) ?? [];
        result.kidSchools = object.kidSchools?.map((x) => this.loadKidSchoolFromObject(x, result.rows.filter(z => z.rowType === InstructorTableRowType.INSTRUCTOR
            && z.cells.some(y => y.cellType === InstructorTableCellType.KID_SCHOOL && y.kidSchoolId === x.id))
            .map(z => z.instructorId))) ?? [];
        if (!result.rows.filter(x => x.rowType ===
            InstructorTableRowType.INSTRUCTOR).some(x => x.instructorId === store.getters['user/userId'])) {
            const currentUserRow = new InstructorTableInstructorRow(result);
            currentUserRow.instructorId = store.getters['user/userId'];
            currentUserRow.addedDate = moment();
            currentUserRow.cells = [];
            result.rows.push(this.loadRowFromObject(currentUserRow, result, hours.pauses));
        }
        return result;
    }
    static loadRowFromObject(object, table, pauses) {
        switch (object.rowType) {
            case InstructorTableRowType.INSTRUCTOR:
                {
                    let row = new InstructorTableInstructorRow(table);
                    row.addedDate = moment(object.addedDate, 'YYYY-MM-DD HH:mm:ss');
                    row.instructorId = object.instructorId;
                    row.cells = [
                        ...object.cells.map((x) => this.loadCellFromObject(x, row)),
                        ...pauses.map(x => {
                            const result = new InstructorTablePauseCell(row);
                            result.hour = x;
                            return result;
                        }),
                    ];
                    return row;
                }
            case InstructorTableRowType.BLOCK:
                {
                    let row = new InstructorTableBlockRow(table);
                    row.blockId = object.blockId;
                    row.cells = [
                        ...object.cells.map((x) => this.loadCellFromObject(x, row)),
                        ...pauses.map(x => { const result = new InstructorTablePauseCell(row); result.hour = x; return result; })
                    ];
                    return row;
                }
            default:
                throw new Error('Undefined type');
        }
    }
    static loadCellFromObject(object, row) {
        switch (object.cellType) {
            case InstructorTableCellType.AVAILABLE:
                {
                    let result = new InstructorTableAvailableCell(row);
                    result.hour = object.hour;
                    return result;
                }
            case InstructorTableCellType.RESERVED:
                {
                    let result = new InstructorTableReservedCell(row);
                    result.hour = object.hour;
                    result.clients = object.clients;
                    result.length = object.length;
                    result.service = object.service;
                    result.note = object.note;
                    result.phone = object.phone;
                    result.isPrepaid = object.isPrepaid ?? false;
                    result.voucher = object.voucher
                        ? store.getters['voucher/get'](object.voucher)
                        : null;
                    result.voucherBlockedValue = object.voucherBlockedValue ?? 0;
                    return result;
                }
            case InstructorTableCellType.PAUSE:
                {
                    let result = new InstructorTablePauseCell(row);
                    result.hour = object.hour;
                    return result;
                }
            case InstructorTableCellType.KID_SCHOOL:
                {
                    let result = new InstructorTableKidSchoolCell();
                    result.hour = object.hour;
                    result.kidSchoolId = object.kidSchoolId;
                    result.length = object.length;
                    return result;
                }
            default:
                throw new Error('Undefined type');
        }
    }
    static loadKidSchoolFromObject(object, instructorIds) {
        let result = new InstructorTableKidSchool();
        result.id = object.id;
        result.duration = object.length;
        result.hourFrom = moment(object.dateTime).hour();
        result.minuteFrom = moment(object.dateTime).minute();
        result.capacity = object.capacity;
        result.age = object.age;
        result.instructorMaxCount = object.instructorsCount;
        result.instructorIds = instructorIds;
        result.kids = object.kids;
        result.finalKidCount = object.finalKidCount;
        result.pricePerKid = object.price;
        result.isPremium = object.isPremium;
        result.leaderId = object.leaderId;
        result.isPrepaidOnly = object.isPrepaidOnly;
        return result;
    }
}
